import Link from 'next/link';
import { useState, type RefObject } from 'react';
import { FlareButton } from './Effects';
import { SOFT_ATMOSPHERE } from './effects/flare/options';
import { Copy } from './CopyEditor';
import { useMarketingRuntime } from './runtime';
import PrivacyChoicesTrigger from '~/components/consent/PrivacyChoicesTrigger';

const footerLinkGroups = [
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Contact', href: '/contact' },
      {
        label: 'LinkedIn',
        href: 'https://www.linkedin.com/company/directordeck/',
      },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Cookie Policy', href: '/cookies' },
    ],
  },
];

export function MarketingHeader({
  home,
  menu,
  menuButton,
  onMenu,
  onNav,
  onLogin,
  onPricing,
  onEvaluation,
  flareEnabled,
  paused,
}: {
  home: boolean;
  menu: boolean;
  menuButton: RefObject<HTMLButtonElement>;
  onMenu: () => void;
  onNav: () => void;
  onLogin: () => void;
  onPricing: () => void;
  onEvaluation: () => void;
  flareEnabled: boolean;
  paused: boolean;
}) {
  const sectionHref = (section: string) => `${home ? '' : '/'}#${section}`;

  return (
    <header className="header wrap">
      <Link
        href="/"
        prefetch={false}
        className="brand"
        aria-label="DirectorDeck home"
      >
        <img src="/landing/assets/logo.svg" alt="DirectorDeck" />
      </Link>
      <nav
        id="marketing-navigation"
        data-test="mobile-menu-content"
        className={menu ? 'nav open' : 'nav'}
        aria-label="Main navigation"
      >
        <Link prefetch={false} href={sectionHref('features')} onClick={onNav}>
          <Copy id="nav.features">Features</Copy>
        </Link>
        <Link prefetch={false} href={sectionHref('examples')} onClick={onNav}>
          <Copy id="nav.decks">Our Decks</Copy>
        </Link>
        <button data-test="marketing-pricing" onClick={onPricing}>
          <Copy id="nav.pricing">Pricing</Copy>
        </button>
        <Link prefetch={false} href={sectionHref('faq')} onClick={onNav}>
          <Copy id="nav.faq">FAQ</Copy>
        </Link>
      </nav>
      <div className="header-actions">
        <button
          data-test="marketing-login"
          className="text-button login"
          onClick={onLogin}
        >
          <Copy id="nav.login">Login</Copy>
        </button>
        <FlareButton
          enabled={flareEnabled}
          paused={paused}
          onClick={onEvaluation}
          style={SOFT_ATMOSPHERE}
          compact
          data-test="marketing-evaluation"
        >
          <Copy id="nav.cta">Get Started</Copy>
        </FlareButton>
        <button
          ref={menuButton}
          data-test="mobile-menu-button"
          data-state={menu ? 'open' : 'closed'}
          aria-controls="marketing-navigation"
          className="menu-button"
          onClick={onMenu}
          aria-expanded={menu}
          aria-label="Toggle navigation"
        >
          <Copy id={menu ? 'nav.close' : 'nav.menu'}>
            {menu ? 'Close' : 'Menu'}
          </Copy>
        </button>
      </div>
    </header>
  );
}

export function MarketingFooter() {
  const runtime = useMarketingRuntime();
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState('');

  return (
    <footer className="site-footer">
      <svg
        className="footer-wordmark-inverse"
        viewBox="0 0 1600 218"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="DirectorDeck"
      >
        <text
          className="footer-wordmark-inverse-text"
          x="796.5"
          y="218"
          textAnchor="middle"
          textLength="1593"
          lengthAdjust="spacing"
        >
          <tspan>DI</tspan>
          <tspan dx="10">RECTORDECK</tspan>
        </text>
      </svg>
      <div className="footer-main-surface">
        <div className="site-footer-main wrap">
          <section
            className="footer-subscribe"
            aria-labelledby="footer-subscribe-title"
          >
            <h2 className="footer-subscribe-copy" id="footer-subscribe-title">
              <Copy id="footer.subscribe.heading">
                Get presentation tips and product updates.
              </Copy>
            </h2>
            <form
              className="footer-subscribe-form"
              onSubmit={async (event) => {
                event.preventDefault();
                if (subscribing || !runtime.subscribe) return;
                const form = event.currentTarget;
                const email = (
                  form.elements.namedItem('email_address') as HTMLInputElement
                ).value;
                setSubscribing(true);
                setSubscribeStatus('');
                try {
                  await runtime.subscribe(email);
                  setSubscribeStatus('You’re subscribed. Thank you!');
                  form.reset();
                } catch {
                  setSubscribeStatus(
                    'Unable to subscribe right now. Please try again.',
                  );
                } finally {
                  setSubscribing(false);
                }
              }}
              aria-busy={subscribing}
            >
              <label className="sr-only" htmlFor="footer-email">
                Email address
              </label>
              <div className="footer-subscribe-field">
                <input
                  id="footer-email"
                  name="email_address"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  required
                />
                <button type="submit" disabled={subscribing}>
                  {subscribing ? (
                    'Subscribing…'
                  ) : (
                    <Copy id="footer.subscribe.cta">Subscribe</Copy>
                  )}
                </button>
              </div>
              <p
                className="footer-form-status"
                role="status"
                aria-live="polite"
              >
                {subscribeStatus}
              </p>
            </form>
          </section>

          <nav className="footer-link-groups" aria-label="Footer navigation">
            {footerLinkGroups.map((group) => (
              <div className="footer-link-group" key={group.title}>
                <h3>
                  <Copy id={`footer.group.${group.title.toLowerCase()}`}>
                    {group.title}
                  </Copy>
                </h3>
                {group.links.map((link, index) => (
                  <a
                    href={link.href}
                    target={
                      link.href.startsWith('https:') ? '_blank' : undefined
                    }
                    rel={
                      link.href.startsWith('https:') ? 'noreferrer' : undefined
                    }
                    key={link.href}
                  >
                    <Copy
                      id={`footer.group.${group.title.toLowerCase()}.${index}`}
                    >
                      {link.label}
                    </Copy>
                  </a>
                ))}
                {group.title === 'Legal' && (
                  <PrivacyChoicesTrigger variant="footer" />
                )}
              </div>
            ))}
            <p className="footer-copyright">
              <Copy id="footer.copyright">
                © 2026 DirectorDeck. All rights reserved.
              </Copy>
            </p>
          </nav>
        </div>
      </div>
    </footer>
  );
}
